import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Type, Square, Circle } from 'lucide-react';
import { PX_PER_SEC, LAYER_H } from './constants';

const ICONS = { text: Type, rect: Square, circle: Circle };

export const LayerBlock = ({ layer, totalDuration, isSelected, onClick, onUpdate }) => {
  const blockRef = useRef(null);
  const [dragMode, setDragMode] = useState(null); // 'move' | 'resize-left' | 'resize-right'
  const dragStart = useRef(null);

  const start = layer.start ?? 0;
  const end = layer.end >= 999 ? totalDuration : layer.end;
  const x = start * PX_PER_SEC;
  const w = Math.max((end - start) * PX_PER_SEC, 16);
  const Icon = ICONS[layer.type] || Square;

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  const snap = (val) => Math.round(val * 4) / 4; // snap to 0.25s

  const onPointerDown = useCallback((e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
    setDragMode(mode);
    dragStart.current = {
      mouseX: e.clientX,
      start,
      end: layer.end >= 999 ? totalDuration : layer.end,
    };
    if (e.target.setPointerCapture && e.pointerId != null) {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  }, [start, layer.end, totalDuration, onClick]);

  useEffect(() => {
    if (!dragMode) return;
    const onMove = (e) => {
      const d = dragStart.current;
      if (!d) return;
      const dx = (e.clientX - d.mouseX) / PX_PER_SEC;

      if (dragMode === 'move') {
        const dur = d.end - d.start;
        const newStart = clamp(snap(d.start + dx), 0, totalDuration - dur);
        const newEnd = newStart + dur;
        onUpdate(layer.id, {
          start: parseFloat(newStart.toFixed(2)),
          end: parseFloat(newEnd.toFixed(2)),
        });
      } else if (dragMode === 'resize-left') {
        const newStart = clamp(snap(d.start + dx), 0, d.end - 0.25);
        onUpdate(layer.id, { start: parseFloat(newStart.toFixed(2)) });
      } else if (dragMode === 'resize-right') {
        const newEnd = clamp(snap(d.end + dx), d.start + 0.25, totalDuration);
        onUpdate(layer.id, { end: parseFloat(newEnd.toFixed(2)) });
      }
    };
    const onUp = () => setDragMode(null);
    // Use pointer events so touch drag on mobile works without the browser
    // hijacking the gesture to pan the page. `touch-action: none` on the
    // handles (set below) tells the browser to leave the gesture to us.
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragMode, totalDuration, layer.id, onUpdate]);

  const typeColor = layer.type === 'text' ? 'bg-indigo-500/60 border-indigo-400/60'
    : layer.type === 'circle' ? 'bg-pink-500/60 border-pink-400/60'
    : 'bg-violet-500/60 border-violet-400/60';

  const label = (layer.content || layer.type).slice(0, 14);

  return (
    <div
      ref={blockRef}
      className={`absolute top-0.5 rounded select-none border transition-shadow ${typeColor} ${
        isSelected ? 'ring-1 ring-white/60 shadow-lg brightness-125' : 'hover:brightness-110'
      } ${dragMode === 'move' ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{ left: x, width: w, height: LAYER_H - 4, touchAction: 'none' }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onPointerDown={(e) => onPointerDown(e, 'move')}
      data-testid={`layer-block-${layer.id}`}
    >
      {/* Left resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-white/30 rounded-l z-10"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => onPointerDown(e, 'resize-left')}
        data-testid={`layer-resize-left-${layer.id}`}
      />
      {/* Right resize handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-white/30 rounded-r z-10"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => onPointerDown(e, 'resize-right')}
        data-testid={`layer-resize-right-${layer.id}`}
      />
      {/* Content */}
      <div className="px-1.5 flex items-center gap-0.5 h-full truncate relative z-0 pointer-events-none">
        <Icon className="h-2.5 w-2.5 text-white/80 flex-shrink-0" />
        <span className="text-[8px] text-white truncate drop-shadow-sm">{label}</span>
      </div>
    </div>
  );
};
